'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSave,
    MdDescription,
    MdAdd,
    MdDelete,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createGuide,
    updateGuide,
    getGuide,
    CreateGuideData,
    UpdateGuideData,
    GuideType,
    GuideOrigin,
    GUIDE_TYPE_LABELS,
    GUIDE_ORIGIN_LABELS,
} from '@/lib/api/billing/guide-api'
import { ClientSelector } from '@/components/client/ClientSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { getHealthPlans, HealthPlan } from '@/lib/api/medical/health-plan-api'
import { format } from 'date-fns'

interface GuideItem {
    procedureCode: string
    procedureName: string
    quantity: number
    unitValue: number
    executionDate?: string
    notes?: string
}

interface FormData {
    healthPlanId: string
    clientId: string
    professionalId: string
    guideType: GuideType
    origin: GuideOrigin
    requestDate: string
    mainOrderCode: string
    mainOrderName: string
    operatorNumber: string
    authorizationNumber: string
    authorizationDate: string
    executionDate: string
    validUntil: string
}

export default function GuideFormPage() {
    const { user, isProfessional } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([])
    const [items, setItems] = useState<GuideItem[]>([])

    const [formData, setFormData] = useState<FormData>({
        healthPlanId: '',
        clientId: '',
        professionalId: '',
        guideType: 'CONSULTA',
        origin: 'MANUAL',
        requestDate: format(new Date(), 'yyyy-MM-dd'),
        mainOrderCode: '',
        mainOrderName: '',
        operatorNumber: '',
        authorizationNumber: '',
        authorizationDate: '',
        executionDate: '',
        validUntil: '',
    })

    useEffect(() => {
        if (user?.companyId) {
            loadData()
            if (isEditing && id) {
                loadGuide()
            }
        }
    }, [id, isEditing, user?.companyId])

    // Quem está logado como profissional é o profissional da guia: não há o que
    // escolher, e o seletor fica escondido (ver o formulário abaixo).
    useEffect(() => {
        if (isEditing || !isProfessional || !user?.professionalId) return
        setFormData(prev =>
            prev.professionalId
                ? prev
                : { ...prev, professionalId: String(user.professionalId) }
        )
    }, [isEditing, isProfessional, user?.professionalId])

    const loadData = async () => {
        if (!user?.companyId) return

        try {
            const healthPlansRes = await getHealthPlans(user.organizationId)
            if (healthPlansRes.success)
                setHealthPlans(healthPlansRes.result || [])
        } catch (error) {
            console.error('Erro ao carregar dados:', error)
        }
    }

    const loadGuide = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getGuide(parseInt(id, 10))
            if (response.success && response.result) {
                const guide = response.result
                setFormData({
                    healthPlanId: guide.healthPlanId?.toString() || '',
                    clientId: guide.clientId?.toString() || '',
                    professionalId: guide.professionalId?.toString() || '',
                    guideType: guide.guideType || 'CONSULTA',
                    origin: guide.origin || 'MANUAL',
                    requestDate: guide.requestDate
                        ? format(new Date(guide.requestDate), 'yyyy-MM-dd')
                        : '',
                    mainOrderCode: guide.mainOrderCode || '',
                    mainOrderName: guide.mainOrderName || '',
                    operatorNumber: guide.operatorNumber || '',
                    authorizationNumber: guide.authorizationNumber || '',
                    authorizationDate: guide.authorizationDate
                        ? format(new Date(guide.authorizationDate), 'yyyy-MM-dd')
                        : '',
                    executionDate: guide.executionDate
                        ? format(new Date(guide.executionDate), 'yyyy-MM-dd')
                        : '',
                    validUntil: guide.validUntil
                        ? format(new Date(guide.validUntil), 'yyyy-MM-dd')
                        : '',
                })
                if (guide.items) {
                    setItems(
                        guide.items.map(item => ({
                            procedureCode: item.procedureCode,
                            procedureName: item.procedureName,
                            quantity: item.quantity,
                            unitValue: item.unitValue,
                            executionDate: item.executionDate
                                ? format(
                                    new Date(item.executionDate),
                                    'yyyy-MM-dd'
                                )
                                : undefined,
                            notes: item.notes,
                        }))
                    )
                }
            } else {
                toast.error(response.error || 'Erro ao carregar guia')
                navigate('/billing/guides')
            }
        } catch (error) {
            toast.error('Erro ao carregar guia')
            navigate('/billing/guides')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const addItem = () => {
        setItems(prev => [
            ...prev,
            {
                procedureCode: '',
                procedureName: '',
                quantity: 1,
                unitValue: 0,
            },
        ])
    }

    const removeItem = (index: number) => {
        setItems(prev => prev.filter((_, i) => i !== index))
    }

    const updateItem = (
        index: number,
        field: keyof GuideItem,
        value: string | number
    ) => {
        setItems(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        )
    }

    const validateForm = (): boolean => {
        if (!formData.healthPlanId) {
            toast.error('Selecione um convenio')
            return false
        }
        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdateGuideData = {
                    guideType: formData.guideType,
                    operatorNumber: formData.operatorNumber || undefined,
                    authorizationNumber:
                        formData.authorizationNumber || undefined,
                    authorizationDate: formData.authorizationDate || undefined,
                    executionDate: formData.executionDate || undefined,
                    validUntil: formData.validUntil || undefined,
                    mainOrderCode: formData.mainOrderCode || undefined,
                    mainOrderName: formData.mainOrderName || undefined,
                }

                const response = await updateGuide(parseInt(id, 10), updateData)
                if (response.success) {
                    toast.success('Guia atualizada com sucesso!')
                    navigate(`/billing/guides/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar guia')
                }
            } else {
                const createData: CreateGuideData = {
                    healthPlanId: parseInt(formData.healthPlanId, 10),
                    clientId: parseInt(formData.clientId, 10),
                    professionalId: formData.professionalId
                        ? parseInt(formData.professionalId, 10)
                        : undefined,
                    guideType: formData.guideType,
                    origin: formData.origin,
                    requestDate: formData.requestDate || undefined,
                    mainOrderCode: formData.mainOrderCode || undefined,
                    mainOrderName: formData.mainOrderName || undefined,
                    items:
                        items.length > 0
                            ? items.filter(
                                item =>
                                    item.procedureCode && item.procedureName
                            )
                            : undefined,
                }

                const response = await createGuide(createData)
                if (response.success && response.result) {
                    toast.success('Guia criada com sucesso!')
                    navigate(`/billing/guides/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar guia')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar guia')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da guia...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/billing/guides')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Guia TISS' : 'Nova Guia TISS'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações da guia'
                                : 'Preencha os dados da nova guia'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Basic Info */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações da Guia
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo de Guia{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.guideType}
                                    onChange={e =>
                                        updateFormField(
                                            'guideType',
                                            e.target.value as GuideType
                                        )
                                    }
                                >
                                    {(
                                        Object.keys(
                                            GUIDE_TYPE_LABELS
                                        ) as GuideType[]
                                    ).map(type => (
                                        <option key={type} value={type}>
                                            {GUIDE_TYPE_LABELS[type]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Origem
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.origin}
                                    onChange={e =>
                                        updateFormField(
                                            'origin',
                                            e.target.value as GuideOrigin
                                        )
                                    }
                                    disabled={isEditing}
                                >
                                    {(
                                        Object.keys(
                                            GUIDE_ORIGIN_LABELS
                                        ) as GuideOrigin[]
                                    ).map(origin => (
                                        <option key={origin} value={origin}>
                                            {GUIDE_ORIGIN_LABELS[origin]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Convenio{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId}
                                    onChange={e =>
                                        updateFormField(
                                            'healthPlanId',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                >
                                    <option value="">Selecione...</option>
                                    {healthPlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Paciente{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <ClientSelector
                                    value={formData.clientId ? Number(formData.clientId) : null}
                                    onChange={(id) => updateFormField('clientId', id ? String(id) : '')}
                                    label=""
                                    placeholder="Selecione..."
                                    disabled={isEditing}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) => updateFormField('professionalId', id ? String(id) : '')}
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={isEditing}
                                    />
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data da Solicitação
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.requestDate}
                                    onChange={e =>
                                        updateFormField(
                                            'requestDate',
                                            e.target.value
                                        )
                                    }
                                    disabled={isEditing}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Authorization */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Autorização</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Número da Autorização
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Número da autorização"
                                    value={formData.authorizationNumber}
                                    onChange={e =>
                                        updateFormField(
                                            'authorizationNumber',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Número da Operadora
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Número na operadora"
                                    value={formData.operatorNumber}
                                    onChange={e =>
                                        updateFormField(
                                            'operatorNumber',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data Autorização
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.authorizationDate}
                                    onChange={e =>
                                        updateFormField(
                                            'authorizationDate',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data Execução
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.executionDate}
                                    onChange={e =>
                                        updateFormField(
                                            'executionDate',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Validade
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validUntil}
                                    onChange={e =>
                                        updateFormField(
                                            'validUntil',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Order */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Procedimento Principal
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código TUSS
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Código do procedimento"
                                    value={formData.mainOrderCode}
                                    onChange={e =>
                                        updateFormField(
                                            'mainOrderCode',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome do Procedimento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Nome do procedimento"
                                    value={formData.mainOrderName}
                                    onChange={e =>
                                        updateFormField(
                                            'mainOrderName',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Items */}
                {!isEditing && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="card-title text-lg">
                                    Itens da Guia
                                </h2>
                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="btn btn-secondary btn-sm gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar Item
                                </button>
                            </div>

                            {items.length === 0 ? (
                                <div className="text-center py-6 text-base-content/60">
                                    <p>Nenhum item adicionado</p>
                                    <p className="text-sm mt-1">
                                        Clique em "Adicionar Item" para incluir
                                        procedimentos
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {items.map((item, index) => (
                                        <div
                                            key={index}
                                            className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <span className="badge badge-secondary">
                                                    Item {index + 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeItem(index)
                                                    }
                                                    className="btn btn-ghost btn-xs text-error"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                <div className="form-control">
                                                    <label className="label py-1">
                                                        <span className="label-text text-xs">
                                                            Código
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm"
                                                        value={
                                                            item.procedureCode
                                                        }
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                'procedureCode',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-control md:col-span-2">
                                                    <label className="label py-1">
                                                        <span className="label-text text-xs">
                                                            Procedimento
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm"
                                                        value={
                                                            item.procedureName
                                                        }
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                'procedureName',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-control">
                                                    <label className="label py-1">
                                                        <span className="label-text text-xs">
                                                            Quantidade
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="input input-bordered input-sm"
                                                        value={item.quantity}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                'quantity',
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 1
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                                                <div className="form-control">
                                                    <label className="label py-1">
                                                        <span className="label-text text-xs">
                                                            Valor Unitário (R$)
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className="input input-bordered input-sm"
                                                        value={item.unitValue}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                'unitValue',
                                                                parseFloat(
                                                                    e.target
                                                                        .value
                                                                ) || 0
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-control">
                                                    <label className="label py-1">
                                                        <span className="label-text text-xs">
                                                            Data Execução
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="input input-bordered input-sm"
                                                        value={
                                                            item.executionDate ||
                                                            ''
                                                        }
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                'executionDate',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-control">
                                                    <label className="label py-1">
                                                        <span className="label-text text-xs">
                                                            Observação
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm"
                                                        value={item.notes || ''}
                                                        onChange={e =>
                                                            updateItem(
                                                                index,
                                                                'notes',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/billing/guides')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
